import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiArrowDownLeft, FiArrowUpRight, FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useChain } from '../context/ChainContext';
import './NotificationDialog.css';

const NotificationDialog = ({ isOpen, onClose, notifications }) => {
  const navigate = useNavigate();
  const { selectedChain, isTestnet } = useChain();
  const { formatAmount } = useWallet();

  const getExplorerUrl = (txHash) => {
    const baseUrl = isTestnet 
      ? (selectedChain === 'TON' ? 'https://testnet.tonscan.org/tx/' : 'https://testnet.bscscan.com/tx/')
      : (selectedChain === 'TON' ? 'https://tonscan.org/tx/' : 'https://bscscan.com/tx/');
    return `${baseUrl}${txHash}`;
  };

  const formatTimeAgo = (timestamp) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getTokenSymbol = (tokenAddress) => {
    // Add logic to get token symbol from address if needed
    return tokenAddress || selectedChain;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="notification-dialog-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="notification-dialog"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="notification-dialog-header">
              <h2>Recent Activity</h2>
              <motion.button
                className="close-button"
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiX />
              </motion.button>
            </div>

            <div className="notification-list">
          {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    className="notification-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="notification-icon">
                      {notification.type === 'receive' ? (
                        <div className="icon-circle receive">
                          <FiArrowDownLeft />
                        </div>
                      ) : (
                        <div className="icon-circle send">
                          <FiArrowUpRight />
                        </div>
                      )}
                    </div>

                    <div className="notification-content">
                      <div className="notification-main">
                        <span className="notification-type">
                          {notification.type === 'receive' ? 'Received' : 'Sent'}
                        </span>
                        <span className={`notification-amount ${notification.type}`}>
                          {notification.type === 'receive' ? '+' : '-'}
                          {formatAmount(notification.amount)} {getTokenSymbol(notification.tokenAddress)}
                        </span>
                      </div>

                      <div className="notification-details">
                        <span className="notification-time">
                          {formatTimeAgo(notification.timestamp)}
                        </span>
                        {notification.from && (
                          <span className="notification-address">
                            {notification.type === 'receive' ? 'From: ' : 'To: '}
                            {notification.from.slice(0, 6)}...{notification.from.slice(-4)}
                          </span>
                        )}
                      </div>

                      <div className="notification-actions">
                        <motion.button
                          className="action-button"
                          onClick={() => window.open(getExplorerUrl(notification.id), '_blank')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <FiExternalLink />
                          <span>View on Explorer</span>
                        </motion.button>
                        {notification.tokenAddress && (
                          <motion.button
                            className="action-button"
                            onClick={() => {
                              navigate(`/token/${getTokenSymbol(notification.tokenAddress)}`);
                              onClose();
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span>View Token</span>
                          </motion.button>
                        )}
                      </div>
              </div>
                  </motion.div>
            ))
          ) : (
                <div className="empty-state">
                  <p>No recent transactions</p>
                </div>
          )}
        </div>
      </motion.div>
    </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationDialog; 